/**
 * Localização pt-BR do SDK FaceTec, mantida por nós.
 *
 * Substitui o `FaceTecStrings.pt-br.js` servido pelo CDN do fornecedor. O
 * arquivo remoto seguia um padrão de escrita próprio (Title Case, várias
 * instruções na mesma frase, jargão técnico) incompatível com o contrato de
 * conteúdo definido para o portal, e mudava sem aviso junto com a versão do
 * SDK. Assumindo o objeto aqui, a redação passa a ser decisão nossa.
 *
 * Regras aplicadas, iguais às de `translateSelfie.js` (Identy):
 *   - caixa de frase, nunca Title Case;
 *   - uma instrução por vez;
 *   - sem jargão biométrico e sem culpar o usuário;
 *   - nenhuma mensagem revela o resultado da análise;
 *   - rótulos de ação em caixa alta, para casar com os botões do portal.
 *
 * O conjunto de chaves espelha o do fornecedor. Ao subir a versão do SDK,
 * comparar as chaves com o arquivo do CDN: uma chave nova ausente aqui volta
 * ao texto padrão em inglês.
 */
export const TRANSLATE_SELFIE_FACE = {
  // Ações
  FaceTec_action_ok: 'OK',
  FaceTec_action_im_ready: 'ESTOU PRONTO',
  FaceTec_action_try_again: 'TENTAR NOVAMENTE',
  FaceTec_action_continue: 'CONTINUAR',
  FaceTec_action_take_photo: 'TIRAR FOTO',
  FaceTec_action_accept_photo: 'ACEITAR',
  FaceTec_action_retake_photo: 'VOLTAR',
  FaceTec_action_confirm: 'CONFIRMAR',

  // Acessibilidade (leitores de tela)
  FaceTec_accessibility_cancel_button: 'Cancelar',
  FaceTec_accessibility_tap_guidance: 'Toque duas vezes na tela para ouvir a orientação de enquadramento.',
  FaceTec_accessibility_key_down_guidance: 'Pressione Enter ou barra de espaço para ouvir a orientação de enquadramento.',
  FaceTec_accessibility_feedback_move_phone_away: 'Rosto muito próximo',
  FaceTec_accessibility_feedback_move_phone_closer: 'Rosto muito distante',
  FaceTec_accessibility_feedback_face_too_far_left: 'Rosto muito à esquerda',
  FaceTec_accessibility_feedback_face_too_far_right: 'Rosto muito à direita',
  FaceTec_accessibility_feedback_face_too_low: 'Rosto muito baixo',
  FaceTec_accessibility_feedback_face_too_high: 'Rosto muito alto',
  FaceTec_accessibility_feedback_face_rotated_too_far_left: 'Rosto muito virado para a esquerda',
  FaceTec_accessibility_feedback_face_rotated_too_far_right: 'Rosto muito virado para a direita',
  FaceTec_accessibility_feedback_face_looking_too_far_left: 'Olhando muito para a esquerda',
  FaceTec_accessibility_feedback_face_looking_too_far_right: 'Olhando muito para a direita',
  FaceTec_accessibility_feedback_face_not_in_camera: 'Rosto fora do enquadramento',
  FaceTec_accessibility_feedback_hold_phone_to_eye_level: 'Segure o aparelho na altura dos olhos',
  FaceTec_accessibility_feedback_move_away_web: 'Rosto muito próximo da câmera',
  FaceTec_accessibility_feedback_move_closer_web: 'Rosto muito distante da câmera',
  // O arquivo do CDN trazia esta chave em espanhol.
  FaceTec_accessibility_feedback_hold_to_eye_level_web: 'Ajuste a câmera na altura dos olhos',

  // Problema com a câmera
  FaceTec_camera_feed_issue_header: 'Não foi possível usar a câmera',
  FaceTec_camera_feed_issue_header_mobile: 'Não foi possível<br/>usar a câmera',
  FaceTec_camera_feed_issue_subheader_message: 'Veja o que pode estar acontecendo:',
  FaceTec_camera_feed_issue_subheader_message_mobile: 'Veja o que pode estar acontecendo.',
  FaceTec_camera_feed_issue_table_header_1: 'O que pode ser',
  FaceTec_camera_feed_issue_table_header_2: 'O que fazer',
  FaceTec_camera_feed_issue_table_row_1_cell_1_firefox_permissions_error: 'O navegador não guardou a permissão da câmera.',
  FaceTec_camera_feed_issue_table_row_1_cell_2_firefox_permissions_error: 'Marque a opção de lembrar a permissão.',
  FaceTec_camera_feed_issue_table_row_1_cell_1: 'A câmera está em uso por outro aplicativo.',
  FaceTec_camera_feed_issue_table_row_1_cell_2: 'Feche o outro aplicativo.',
  FaceTec_camera_feed_issue_table_row_2_cell_1: 'Outro aplicativo está alterando o vídeo.',
  FaceTec_camera_feed_issue_table_row_2_cell_2: 'Feche o outro aplicativo.',
  FaceTec_camera_feed_issue_table_row_3_cell_1: 'Este aparelho não permite a verificação.',
  FaceTec_camera_feed_issue_table_row_3_cell_2: 'Use outro aparelho.',
  // O texto original levava a um domínio de ajuda do fornecedor; o portal não
  // encaminha o usuário para fora do fluxo.
  FaceTec_camera_feed_issue_subtable_message: 'Por segurança, configurações de câmera fora do padrão são bloqueadas.',
  FaceTec_camera_feed_issue_action: 'FECHAR',
  FaceTec_camera_feed_issue_action_firefox_permissions_error: 'OK',
  FaceTec_camera_permission_header: 'Ative a câmera',
  FaceTec_camera_permission_message: 'O acesso à câmera está bloqueado.<br/>Libere a câmera nas configurações do navegador.',

  // Tela cheia
  FaceTec_enter_fullscreen_header: 'Vamos abrir a tela cheia',
  FaceTec_enter_fullscreen_message: 'Toque no botão abaixo para começar.',
  FaceTec_enter_fullscreen_action: 'ABRIR TELA CHEIA',

  // Feedback ao vivo — espelha a tabela usada na Identy
  FaceTec_feedback_center_face: 'Centralize seu rosto',
  FaceTec_feedback_face_not_found: 'Centralize seu rosto',
  FaceTec_feedback_move_phone_away: 'Afaste um pouco',
  FaceTec_feedback_move_away_web: 'Afaste um pouco',
  FaceTec_feedback_move_phone_closer: 'Chegue um pouco mais perto',
  FaceTec_feedback_move_web_closer: 'Chegue um pouco mais perto',
  FaceTec_feedback_move_web_even_closer: 'Chegue mais perto',
  FaceTec_feedback_move_phone_to_eye_level: 'Olhe para a câmera',
  FaceTec_feedback_move_to_eye_level_web: 'Olhe para a câmera',
  FaceTec_feedback_face_not_looking_straight_ahead: 'Olhe para a câmera',
  FaceTec_feedback_face_not_upright: 'Mantenha a cabeça reta',
  FaceTec_feedback_face_not_upright_mobile: 'Mantenha a cabeça reta',
  FaceTec_feedback_hold_steady: 'Mantenha-se imóvel',
  FaceTec_feedback_use_even_lighting: 'Evite sombras no rosto',

  // Documento (não usado no fluxo atual, mantido para não deixar buraco)
  FaceTec_idscan_type_selection_header: 'Vamos escanear<br/>seu documento',
  FaceTec_idscan_capture_id_front_instruction_message: 'Mostre a frente do documento',
  FaceTec_idscan_capture_id_back_instruction_message: 'Mostre o verso do documento',
  FaceTec_idscan_review_id_front_instruction_message: 'Confira se está nítido e legível',
  FaceTec_idscan_review_id_back_instruction_message: 'Confira se está nítido e legível',
  FaceTec_idscan_additional_review_message: 'Precisamos de<br/>mais uma conferência',
  FaceTec_idscan_ocr_confirmation_main_header: 'Confira seus dados',
  FaceTec_idscan_ocr_confirmation_scroll_message: 'Role para baixo',
  FaceTec_idscan_feedback_flip_id_to_back_message: 'Mostre o verso do documento',

  // Inicialização
  FaceTec_initializing_camera: 'Preparando a câmera',

  // Preparação
  FaceTec_instructions_header_ready_desktop: 'Vamos validar sua selfie',
  FaceTec_instructions_header_ready_1_mobile: 'Vamos validar',
  FaceTec_instructions_header_ready_2_mobile: 'sua selfie',
  FaceTec_instructions_message_ready_desktop: 'Enquadre o rosto no oval',
  FaceTec_instructions_message_ready_1_mobile: 'Enquadre o rosto',
  FaceTec_instructions_message_ready_2_mobile: 'no oval',

  // Antes da captura
  FaceTec_presession_frame_your_face: 'Enquadre o rosto no oval',
  FaceTec_presession_look_straight_ahead: 'Olhe para a câmera',
  FaceTec_presession_hold_steady3: 'Mantenha-se imóvel: 3',
  FaceTec_presession_hold_steady2: 'Mantenha-se imóvel: 2',
  FaceTec_presession_hold_steady1: 'Mantenha-se imóvel: 1',
  FaceTec_presession_eyes_straight_ahead: 'Olhe para a câmera',
  FaceTec_presession_remove_dark_glasses: 'Tire os óculos escuros',
  FaceTec_presession_neutral_expression: 'Expressão neutra, sem sorrir',
  FaceTec_presession_conditions_too_bright: 'Evite luz direta no rosto',
  FaceTec_presession_brighten_your_environment: 'Procure um lugar mais iluminado',

  // Resultado — nenhuma mensagem revela o veredito da análise
  FaceTec_result_success_message: 'Tudo certo',
  FaceTec_result_facescan_upload_message: 'Validando...',
  FaceTec_result_idscan_upload_message: 'Validando...',
  FaceTec_result_idscan_unsuccess_message: 'Não foi possível concluir.<br/>Tente novamente.',
  FaceTec_result_idscan_success_front_side_message: 'Documento capturado',
  FaceTec_result_idscan_success_front_side_back_next_message: 'Frente do documento<br/>capturada',
  FaceTec_result_idscan_success_back_side_message: 'Documento capturado',
  FaceTec_result_idscan_success_passport_message: 'Passaporte capturado',
  FaceTec_result_idscan_success_user_confirmation_message: 'Documento com foto<br/>capturado',
  FaceTec_result_idscan_success_additional_review_message: 'Foto do documento<br/>capturada',
  FaceTec_result_idscan_retry_face_did_not_match_message: 'Não foi possível concluir.<br/>Tente novamente.',
  FaceTec_result_idscan_retry_id_not_fully_visible_message: 'O documento não está<br/>totalmente visível',
  FaceTec_result_idscan_retry_ocr_results_not_good_enough_message: 'O texto do documento não está legível',
  FaceTec_result_idscan_retry_id_type_not_suppported_message: 'Documento não aceito.<br/>Use outro documento.',
  FaceTec_result_idscan_retry_barcode_not_read_message: 'Não foi possível ler o código.<br/>Tente novamente.',

  // Retentativa
  FaceTec_retry_header: 'Vamos tentar de novo',
  FaceTec_retry_subheader_message: 'Siga as dicas abaixo',
  FaceTec_retry_your_image_label: 'Sua selfie',
  FaceTec_retry_ideal_image_label: 'Exemplo',
  FaceTec_retry_instruction_message_1: 'Rosto bem iluminado e centralizado',
  FaceTec_retry_instruction_message_2: 'Expressão neutra, sem sorrir',
  FaceTec_retry_instruction_message_3: 'Limpe a lente da câmera'
}
